// Botão "Falar no WhatsApp" no e-mail (specs/24): os mesmos campos na campanha e no template de e-mail.
import { renderizar, WHATSAPP_BOTAO_PADRAO, WHATSAPP_MENSAGEM_PADRAO, type montarContexto } from '@crm/contracts';
import { porGrupo, useStatusClientes } from '../status';

export interface BotaoWhatsappValor {
  botao: boolean; texto: string; mensagem: string; statusId: string; protege: boolean;
}

export const botaoWhatsappInicial = (d?: {
  whatsappBotao?: boolean; whatsappTexto?: string | null; whatsappMensagem?: string | null;
  whatsappStatusId?: string | null; whatsappProtegeAtivo?: boolean;
}): BotaoWhatsappValor => ({
  botao: d?.whatsappBotao ?? false,
  texto: d?.whatsappTexto ?? WHATSAPP_BOTAO_PADRAO,
  mensagem: d?.whatsappMensagem ?? WHATSAPP_MENSAGEM_PADRAO,
  statusId: d?.whatsappStatusId ?? '',
  protege: d?.whatsappProtegeAtivo ?? true,
});

/** Corpo da API (campanha e template) a partir do valor da tela. */
export const botaoWhatsappPayload = (v: BotaoWhatsappValor, usaEmail = true) => ({
  whatsappBotao: usaEmail && v.botao, whatsappTexto: v.texto.trim() || null, whatsappMensagem: v.mensagem.trim() || null,
  whatsappStatusId: v.statusId || null, whatsappProtegeAtivo: v.protege,
});

/** Texto do botão já com as variáveis do exemplo (para a prévia do e-mail). */
export const textoBotaoExemplo = (v: BotaoWhatsappValor, ctx: ReturnType<typeof montarContexto>) =>
  renderizar(v.texto || WHATSAPP_BOTAO_PADRAO, ctx).texto;

export function CamposBotaoWhatsapp({ valor, onChange, ctxExemplo, disabled = false, ondeStatus = 'campanha' }: {
  valor: BotaoWhatsappValor;
  onChange: (v: BotaoWhatsappValor) => void;
  ctxExemplo: ReturnType<typeof montarContexto>;
  disabled?: boolean;
  /** Texto de ajuda do status: campanha ou template */
  ondeStatus?: 'campanha' | 'template';
}) {
  const statusLista = useStatusClientes();
  const set = (p: Partial<BotaoWhatsappValor>) => onChange({ ...valor, ...p });
  return (
    <div className="col" style={{ gap: 12 }}>
      <label className={`opcao${valor.botao ? ' on' : ''}`} style={{ alignItems: 'flex-start' }}>
        <input type="checkbox" checked={valor.botao} disabled={disabled} onChange={(e) => set({ botao: e.target.checked })} style={{ marginTop: 3 }} />
        <span className="col" style={{ gap: 2 }}>
          <span style={{ fontWeight: 500 }}>Botão “Falar no WhatsApp”</span>
          <span className="small muted">Abre a conversa com o vendedor de cada cliente. O CRM registra quem clicou. Vendedor sem WhatsApp cadastrado envia sem o botão.</span>
        </span>
      </label>
      {valor.botao && (
        <div className="col" style={{ gap: 12 }}>
          <label className="field"><span>Texto do botão</span>
            <input className="input" maxLength={40} value={valor.texto} disabled={disabled} onChange={(e) => set({ texto: e.target.value })} />
          </label>
          <label className="field"><span>Mensagem que já chega escrita no WhatsApp do cliente</span>
            <textarea className="textarea" rows={2} maxLength={500} value={valor.mensagem} disabled={disabled} onChange={(e) => set({ mensagem: e.target.value })} />
            <span className="hint">Exemplo: “{renderizar(valor.mensagem || WHATSAPP_MENSAGEM_PADRAO, ctxExemplo).texto}”. O cliente pode editar antes de enviar.</span>
          </label>
          <label className="field"><span>Quem clicar vai para o status</span>
            <select className="select" value={valor.statusId} disabled={disabled} onChange={(e) => set({ statusId: e.target.value })}>
              <option value="">Não alterar o status</option>
              {porGrupo(statusLista.data).filter((g) => g.itens.length).map((g) => (
                <optgroup key={g.grupo} label={g.nome}>{g.itens.map((st) => <option key={st.id} value={st.id}>{st.nome}</option>)}</optgroup>
              ))}
            </select>
          </label>
          {valor.statusId && (
            <label className="check"><input type="checkbox" checked={valor.protege} disabled={disabled} onChange={(e) => set({ protege: e.target.checked })} />Não mudar quem já é cliente ativo</label>
          )}
          <span className="hint">
            {ondeStatus === 'template'
              ? 'Ao usar este template numa campanha, tudo isso vem junto (dá para mudar lá). No e-mail pela ficha, o botão e o status do template valem para aquele envio.'
              : valor.statusId ? 'Depois é só criar outra campanha filtrando por esse status para mandar o segundo e-mail.' : 'O clique fica registrado na ficha e na campanha, sem mudar o status.'}
          </span>
        </div>
      )}
    </div>
  );
}
